import jsPDF from 'jspdf';
import Logo from "../images/hotel_logo.png";
import { HOTEL_NAME, HOTEL_ADDRESS, HOTEL_PHONE } from '../Variables.js';

const generateReceiptPDF = (cart, user, cashierFirstName, cashierLastName, customerName, customerPhone, saleTimestamp) => {
  if (cart.length === 0) {
    throw new Error("No items in cart to generate receipt");
  }

  let headerName = HOTEL_NAME;

  if (cart.length > 0) {
    // Get the department of the first item
    const firstDept = (cart[0].department || "").trim();
    
    // Check if ALL items have the exact same non-empty department
    const allSameDepartment = cart.every(item => {
      const dept = (item.department || "").trim();
      return dept === firstDept && dept !== "";
    });

    if (allSameDepartment && firstDept) {
      headerName = firstDept; // e.g. "Kitchen", "Bar", "Restaurant"
      // Optional: headerName = `${firstDept} Department`; // more formal
    }
  }

  // Convert Firestore Timestamp to JS Date if needed
  const saleDate = saleTimestamp.toDate ? saleTimestamp.toDate() : saleTimestamp;

  const formattedDate = saleDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });

  const formattedTime = saleDate.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true
  });

  const formattedSaleDate = `${formattedDate}, ${formattedTime}`;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [80, 297]
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 5;
  const contentWidth = pageWidth - (2 * margin);
  let yPos = margin;

  try {
    const imgWidth = 20;
    const imgHeight = 20;
    const centerX = (pageWidth - imgWidth) / 2;
    doc.addImage(Logo, 'PNG', centerX, yPos, imgWidth, imgHeight);
    yPos += 25;
  } catch (err) {
    console.log("Logo not found, continuing without it");
  }

  // Header Name (Hotel or Department)
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  
  const maxNameWidth = contentWidth - 2;
  const nameLines = doc.splitTextToSize(headerName.toUpperCase(), maxNameWidth);
  
  nameLines.forEach((line, index) => {
    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
  });

  // Address (always hotel address)
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);

  const addressMaxWidth = contentWidth - 4;
  const wrappedAddress = doc.splitTextToSize(HOTEL_ADDRESS, addressMaxWidth);
  
  yPos += 2;
  
  wrappedAddress.forEach((line) => {
    doc.text(line, pageWidth / 2, yPos, { align: 'center' });
    yPos += 4;
  });

  // Phone (always hotel phone)
  doc.text(`Tel: ${HOTEL_PHONE}`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('SALES RECEIPT', pageWidth / 2, yPos, { align: 'center' });
  yPos += 2;

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(0.2);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const cashierName = `${cashierFirstName} ${cashierLastName}`.trim() || user.email || 'Unknown';

  const details = [
    `Date: ${formattedSaleDate}`,
    `Receipt No: ${Math.floor(Math.random() * 1000000).toString().padStart(6, '0')}`,
    `Cashier: ${cashierName}`,
    ...(customerName ? [`Customer: ${customerName}`] : []),
    ...(customerPhone ? [`Phone: ${customerPhone}`] : [])
  ];

  details.forEach(text => {
    doc.text(text, margin, yPos);
    yPos += 5;
  });

  yPos += 5;

  doc.setFont('helvetica', 'bold');
  doc.text('Item', margin, yPos);
  doc.text('Qty', margin + 38, yPos);
  doc.text('Amount', margin + 50, yPos);
  yPos += 2;

  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;

  doc.setFont('helvetica', 'normal');
  cart.forEach((item) => {
    if (yPos > 250) {
      doc.addPage();
      yPos = margin;
    }

    doc.setFontSize(8);
    
    const itemDisplay = `${item.name} (N${item.price.toLocaleString()})`;
    const itemMaxWidth = 35;
    
    const nameLines = doc.splitTextToSize(itemDisplay, itemMaxWidth);
    
    nameLines.forEach((line, i) => {
      doc.text(line, margin, yPos + (i * 4));
    });
    
    const itemHeight = Math.max(8, nameLines.length * 4);
    
    doc.text(`${item.quantity}`, margin + 40, yPos + (itemHeight - 4) / 2);
    doc.text(`N ${item.subtotal.toLocaleString()}`, margin + 48, yPos + (itemHeight - 4) / 2);

    yPos += itemHeight;
  });

  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 5;

  const calculateTotal = () => {
    return cart.reduce((total, item) => total + item.subtotal, 0);
  };

  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', margin, yPos);
  doc.text(`N ${calculateTotal().toLocaleString()}`, margin + 42, yPos);
  yPos += 10;

  doc.setFont('helvetica', 'italic');
  doc.setFontSize(9);
  
  const thankYouMessage = 'Thank you for your patronage!';
  const thankYouLines = doc.splitTextToSize(thankYouMessage, contentWidth - 10);
  
  thankYouLines.forEach((line, index) => {
    doc.text(line, pageWidth / 2, yPos + (index * 4), { align: 'center' });
  });
  
  yPos += thankYouLines.length * 4 + 5;

  doc.setFontSize(8);
  doc.setTextColor(100, 100, 100);
  
  const generatedText = `Generated on ${new Date().toLocaleString()}`;
  const generatedLines = doc.splitTextToSize(generatedText, contentWidth - 10);
  
  generatedLines.forEach((line, index) => {
    doc.text(line, pageWidth / 2, yPos + (index * 4), { align: 'center' });
  });

  return doc;
};

export default generateReceiptPDF;